export type Hex = `0x${string}`;

/**
 * A single creator reward recipient with their share in basis points (bps).
 * ETH-v1 fair launch markets accept 1–5 recipients; total must be exactly 10 000 bps.
 */
export interface RecipientEntry {
  address: Hex;
  bps: number;
}

/** A validated token configuration ready to launch. Supply is in WHOLE tokens. */
export interface TokenConfig {
  name: string;
  symbol: string;
  supplyWhole: bigint;
  /**
   * Creator reward recipients for ETH-v1 fair-launch markets.
   * If omitted the launcher defaults to a single recipient (the launching wallet)
   * at 10 000 bps.  1–5 entries; total bps must equal 10 000.
   */
  recipients?: RecipientEntry[];
}

/** Optional off-chain metadata, local to the interface (never written on-chain). */
export interface TokenMetadata {
  description?: string;
  website?: string;
  x?: string;
  telegram?: string;
  logoDataUrl?: string;
}

export interface FactoryStatus {
  configured: boolean;
  address: Hex | null;
  chainId: number;
  bytecodeVerified: boolean;
  /** keccak256 of the on-chain runtime code, when a factory is configured. */
  observedBytecodeHash: Hex | null;
  paused: boolean;
  launchFee: bigint;
  owner: Hex | null;
  demo: boolean;
}

export interface GasEstimate {
  /** Raw gas units from estimateContractGas (NOT wei). */
  gasUnits: bigint;
  /** Current gas price in wei. */
  gasPriceWei: bigint;
  /** gasUnits * gasPriceWei — the actual network cost in wei. */
  estimatedGasCostWei: bigint;
  /** True for mock/demonstration estimates. */
  demonstration?: boolean;
}

export interface FeeQuote {
  effectiveFee: bigint;
  exempt: boolean;
  publicFee: bigint;
}

/** A single named verification check with the values it compared. */
export interface VerificationCheck {
  key: string;
  label: string;
  passed: boolean;
  expected?: string;
  observed?: string;
}

/** Result of running the full post-deploy verification. */
export interface VerificationResult {
  passed: boolean;
  checks: VerificationCheck[];
  failures: VerificationCheck[];
}

export interface LaunchReceipt {
  tokenAddress: Hex;
  /** Present only for ETH-v1 fair-launch markets (createMarket path). */
  marketAddress?: Hex;
  creator: Hex;
  factoryAddress: Hex | null;
  name: string;
  symbol: string;
  decimals: number;
  supplyWhole: bigint;
  supplySmallestUnits: bigint;
  feePaid: bigint;
  txHash: Hex;
  blockNumber: bigint;
  chainId: number;
  timestamp: number;
  expectedFactoryHash: Hex | null;
  observedFactoryHash: Hex | null;
  transactionStatus: "success" | "reverted";
  demonstrationMode: boolean;
}

/**
 * The outcome of a launch attempt that reached a confirmed receipt. Explicitly
 * separates "the transaction confirmed" from "verification passed" so the UI can
 * never present a confirmed-but-unverified launch as a verified success.
 */
export interface LaunchOutcome {
  transactionConfirmed: boolean;
  verificationPassed: boolean;
  verificationFailures: VerificationCheck[];
  verification: VerificationResult;
  receipt: LaunchReceipt;
}

export interface HistoryPage {
  items: LaunchSummary[];
  /** True when one or more block ranges failed after retries. */
  partial: boolean;
  failedRanges: Array<{ from: string; to: string }>;
  /** Upper bound (exclusive) for the next older page, or null when exhausted. */
  nextCursor: bigint | null;
  /** Confirmation threshold applied (recent blocks excluded). */
  confirmations: number;
}

export interface LaunchSummary {
  tokenAddress: Hex;
  creator: Hex;
  name: string;
  symbol: string;
  supplySmallestUnits: bigint;
  txHash: Hex;
  blockNumber: bigint;
  logIndex: number;
}

/** Distinct, user-visible states of a real deploy. */
export type DeployPhase =
  | "preparing"
  | "revalidatingFactory"
  | "estimatingGas"
  | "checkingBalance"
  | "awaitingApproval"
  | "submitted"
  | "waitingReceipt"
  | "replaced"
  | "verifying"
  | "confirmed"
  | "reverted";

export type LauncherErrorKind =
  | "userRejected"
  | "wrongNetwork"
  | "insufficientBalance"
  | "factoryNotConfigured"
  | "factoryPaused"
  | "bytecodeMismatch"
  | "reverted"
  | "replaced"
  | "rpcError"
  | "simulationRevert"
  | "feeChanged"
  | "postVerificationFailed"
  | "notOwner"
  | "invalidAddress"
  | "accountMismatch"
  | "zeroAddress"
  | "noChange"
  | "unknown";

export class LauncherError extends Error {
  readonly kind: LauncherErrorKind;
  readonly detail?: Record<string, string>;
  constructor(kind: LauncherErrorKind, message?: string, detail?: Record<string, string>) {
    super(message ?? kind);
    this.name = "LauncherError";
    this.kind = kind;
    if (detail) this.detail = detail;
  }
}

/** Outcome of an admin setFeeExempt confirmation. */
export interface AdminOutcome {
  txHash: Hex;
  account: Hex;
  requestedExempt: boolean;
  /** Receipt confirmed on-chain (status success). */
  transactionConfirmed: boolean;
  eventAccount: Hex | null;
  eventExempt: boolean | null;
  eventUpdatedBy: Hex | null;
  /** On-chain re-read AFTER the tx; null when the read was unavailable. */
  finalOnChainExempt: boolean | null;
  /** True only when event + final-state checks all pass. */
  verificationPassed: boolean;
  verificationFailures: VerificationCheck[];
  /** @deprecated use verificationPassed */
  verified: boolean;
}
